/**
 * Celebration and Confetti Effects
 * Makes completing tasks feel AMAZING! 🎉
 */

import confetti from 'canvas-confetti';

/**
 * Fire confetti celebration
 * @param {string} type - Type of celebration (task, milestone, streak, goal)
 */
export function fireConfetti(type = 'task') {
  const celebrations = {
    task: () => {
      // Simple confetti burst for completing a task
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    },
    
    milestone: () => {
      // Bigger celebration for milestones
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

      function randomInRange(min, max) {
        return Math.random() * (max - min) + min;
      }

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);
    },
    
    streak: () => {
      // Fire and stars for streak milestones
      const defaults = {
        spread: 360,
        ticks: 100,
        gravity: 0,
        decay: 0.94,
        startVelocity: 30,
        shapes: ['star'],
        colors: ['FFE400', 'FFBD00', 'E89400', 'FFCA6C', 'FDFFB8']
      };

      function shoot() {
        confetti({
          ...defaults,
          particleCount: 40,
          scalar: 1.2,
          shapes: ['star']
        });

        confetti({
          ...defaults,
          particleCount: 10,
          scalar: 0.75,
          shapes: ['circle']
        });
      }

      setTimeout(shoot, 0);
      setTimeout(shoot, 100);
      setTimeout(shoot, 200);
    },
    
    goal: () => {
      // Epic celebration for reaching savings goals
      const duration = 5000;
      const animationEnd = Date.now() + duration;

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#bb0000', '#ffffff']
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#bb0000', '#ffffff']
        });

        if (Date.now() < animationEnd) {
          requestAnimationFrame(frame);
        }
      })();
    }
  };

  const celebration = celebrations[type] || celebrations.task;
  celebration();
}

/**
 * Get motivational message based on task completion
 * @param {number} tasksCompleted - Number of tasks completed
 * @param {number} streakDays - Current streak in days
 * @returns {string} Motivational message
 */
export function getMotivationalMessage(tasksCompleted, streakDays = 0) {
  const messages = {
    first: [
      "🎉 Awesome! You completed your first task!",
      "🌟 Great start! Keep it up!",
      "💪 You're off to an amazing start!"
    ],
    regular: [
      "🎯 Nice work! Task complete!",
      "✨ Great job! Keep going!",
      "🚀 You're on fire!",
      "💫 Excellent work!",
      "⭐ Way to go!",
      "🎊 Task crushed!",
      "👏 Fantastic effort!",
      "🌈 You're doing amazing!"
    ],
    milestone: {
      5: "🏆 5 tasks done! You're a superstar!",
      10: "🎖️ 10 tasks complete! Incredible!",
      25: "🥇 25 tasks! You're unstoppable!",
      50: "👑 50 tasks! You're a champion!",
      100: "🏅 100 TASKS! You're a LEGEND!",
      250: "💎 250 tasks! ABSOLUTELY AMAZING!",
      500: "🔥 500 TASKS! You're a SUPERHERO!",
      1000: "🌟 1,000 TASKS! MIND BLOWING!"
    },
    streak: {
      3: "🔥 3 day streak! Keep it going!",
      7: "⚡ FULL WEEK STREAK! Amazing!",
      14: "💪 2 WEEK STREAK! Incredible!",
      30: "🎯 30 DAY STREAK! You're unstoppable!",
      100: "👑 100 DAY STREAK! LEGENDARY!"
    }
  };

  // Check for milestone messages
  if (messages.milestone[tasksCompleted]) {
    return messages.milestone[tasksCompleted];
  }

  // Check for streak messages
  if (streakDays > 0 && messages.streak[streakDays]) {
    return messages.streak[streakDays];
  }

  // First task
  if (tasksCompleted === 1) {
    return messages.first[Math.floor(Math.random() * messages.first.length)];
  }

  // Regular completion
  return messages.regular[Math.floor(Math.random() * messages.regular.length)];
}

/**
 * Determine celebration type based on context
 * @param {number} tasksCompleted - Total tasks completed
 * @param {number} streakDays - Current streak
 * @returns {string} Celebration type
 */
export function getCelebrationType(tasksCompleted, streakDays = 0) {
  // Major milestones
  if ([10, 25, 50, 100, 250, 500, 1000].includes(tasksCompleted)) {
    return 'milestone';
  }

  // Streak celebrations
  if ([7, 14, 30, 100].includes(streakDays)) {
    return 'streak';
  }

  // First task
  if (tasksCompleted === 1) {
    return 'milestone';
  }

  // Regular task
  return 'task';
}

/**
 * Play success sound (optional, can be added later)
 * @param {string} type - Type of sound to play
 */
export function playSuccessSound(type = 'task') {
  // Placeholder for future sound implementation
  // Can use Web Audio API or audio files
  console.log(`🔊 Playing ${type} success sound`);
}

/**
 * Show animated badge popup
 * @param {object} badge - Badge object with emoji, name, description
 * @returns {Promise} Resolves when animation is complete
 */
export function showBadgePopup(badge) {
  return new Promise((resolve) => {
    // This will be handled by the BadgeDisplay component
    // Just return the badge data
    resolve(badge);
  });
}

/**
 * Calculate time until task is "late" for timer display
 * @param {Date|string} dueDate - Task due date
 * @returns {object} Time remaining object
 */
export function getTimeRemaining(dueDate) {
  if (!dueDate) return null;
  
  const now = new Date();
  const due = new Date(dueDate);
  const diff = due - now;
  
  if (diff < 0) {
    return { overdue: true, hours: 0, minutes: 0 };
  }
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return { overdue: false, hours, minutes };
}

/**
 * Get urgency level for visual feedback
 * @param {Date|string} dueDate - Task due date
 * @returns {string} urgency level (normal, soon, urgent, overdue)
 */
export function getTaskUrgency(dueDate) {
  if (!dueDate) return 'normal';
  
  const remaining = getTimeRemaining(dueDate);
  
  if (remaining.overdue) return 'overdue';
  
  const totalMinutes = remaining.hours * 60 + remaining.minutes;
  
  if (totalMinutes < 60) return 'urgent'; // Less than 1 hour
  if (totalMinutes < 180) return 'soon'; // Less than 3 hours
  
  return 'normal';
}

const celebrationHelper = {
  fireConfetti,
  getMotivationalMessage,
  getCelebrationType,
  playSuccessSound,
  showBadgePopup,
  getTimeRemaining,
  getTaskUrgency
};

export default celebrationHelper;

